import React, { FC, useState, useEffect, useRef } from "react";
import { AddBox, Clear, Check, Edit, Delete} from "@mui/icons-material";
import { StyledSectionTable } from './TableInlineEdit.styles';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Input, Button, IconButton, Stack, Autocomplete, Tooltip, Avatar, Grid } from '@mui/material';
import { Utils } from "../../utils/common-utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { fetchAccountSkuByName } from "../../store/inventoryStore/inventoryThunk"
import CustomTextField from "../FormFields/CustomTextField";
import { toast } from "react-toastify";

interface EstimateDeliveryTableInlineEditProps {
  onChangeRow: (data: any) => void;
  rowsData?: any[];
}

const createData = (id:number, name: string, description:string, spec:string, hsnCode: string, unit:string, qty:number, appliedRate:number, total:number, _id:string, skuCode: string) => (
  {
    id,
    name,
    description,
    spec,
    hsnCode,
    unit,
    qty,
    appliedRate,
    total,
    isEditMode: false,
    _id,
    skuCode
  }
);

interface tableCellProps {
  row: any,
  name: string,
  onChange: any,
  align: string,
  type: string,
  isGst?: boolean,
  formatNumber?: boolean,
  readonly?: boolean,
}

const CustomTableCell = ({row, name, onChange, align, type, isGst, formatNumber, readonly}: tableCellProps) => {
  const { isEditMode } = row;
  return (
    <TableCell style={(isGst)? {whiteSpace:'pre-line'} : {}} align={(align==='center')? 'center':'left'} className={'tableCell'}>
      {isEditMode ? (
        <Input
          value={row[name]}
          name={name}
          type={type}
          onChange={e => onChange(e, row, type)}
          className={'inputCell'}
          readOnly={(readonly)? readonly : false}
          error={(type==='text' && row[name] === "")}
        />
      ) : (
        (isGst) ? ((Utils.formatNumber(row[name+'total'], true) || 0)+'\n @ '+(row[name] || 0)+'%') : (formatNumber) ? Utils.formatNumber(row[name], true) : row[name]
      )}
    </TableCell>
  );
};

// TableInlineEdit component. 
const EstimateDeliveryTableInlineEdit: FC<EstimateDeliveryTableInlineEditProps> = ({ onChangeRow, rowsData }) => {

  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [rows, setRows] = useState<any[]>([]);
  const [previous, setPrevious] = useState<any>({});
  const [isRowAdd, setIsRowAdd] = React.useState(false);

  // autocomplete states
  const elmRef = useRef<any>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();
  const [newOption, setNewOption] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const onToggleEditMode = (id:number) => {
    setRows(() => {
      return rows.map(row => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        } 
        return row;
      });
    });
    onChangeRow(rows);
  };

  const onChange = (e:any, row:any, type: string) => {
    if (!previous[row.id]) {
      setPrevious((state: any) => ({ ...state, [row.id]: row }));
    }
    const value = (type!=='number')? e.target.value : (type==='number' && e.target.value>0)? e.target.value : 0;
    const name = e.target.name;
    const { id } = row;
    const newRows = rows.map(row => {
      if (row.id === id) {
        let _qty = (name==='qty')? value : row.qty;
        let _appliedRate = (name==='appliedRate')? value : row.appliedRate;
        let amount = (_qty* _appliedRate)
        row.amount = Number(amount);
        row.total = Number(amount);

        return { ...row, [name]: value };
      }
      return row;
    });
    setRows(newRows);
  };

  const onRevert = (id: number) => {
    const newRows = rows.map(row => {
      if (row.id === id) {
        return previous[id] ? previous[id] : row;
      }
      return row;
    });
    setRows(newRows);
    setPrevious((state: any[]) => {
      delete state[id];
      return state;
    });
    onToggleEditMode(id);
  };

  const onAdd = () => {
    let rowCount = rows.length+1;
    // setRows([...rows, createData(rowCount, "", "", "", "", "", 0, 0, 0, "")])
    if (selected) {
      let _qty = 1;
      let _appliedRate = Number(selected.salesPrice)
      let amount = (_qty* _appliedRate)
      let total = Number(amount);
      // (id:number, name: string, description:string, spec:string, hsnCode: string, unit:string, qty:number, appliedRate:number, total:number, _id:string, skuCode: string)
      
      setRows([...rows, createData(rowCount, selected.name, selected.description, "", selected.hsnCode, selected.salesUnit, 1, selected.salesPrice, total, "", selected._id)])
    } else {
      setRows([...rows, createData(rowCount, "", "", "", "", "", 0, 0, 0, "", "")])
      // setRows([...rows, createData(rowCount, "", "", "", "", "", 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, "", "" "")])
      // toast.success("Please search and select data from Inventory");
    }

    setIsRowAdd(true);
    setSelected('');
  };

  const onDelete = (id: number) => {
    const newRows = rows.filter((obj) => obj.id !== id);
    setRows(newRows);
    onChangeRow(newRows);
  };

  useEffect(() => {
    if(isRowAdd) {
      setIsRowAdd(false);
      let rowCount = rows.length;
      onToggleEditMode(rowCount)
    }
  }, [rows, isRowAdd, onToggleEditMode]);

  useEffect(() => {
    let rowCount = 0;
    setRows([]);
    if(rowsData && rowsData.length>0) {
      console.log('inside.if');
      rowsData.forEach((row) => {
        ++rowCount;
        let _row = createData(rowCount, row.name, row.description, row.spec, row.hsnCode, row.unit, row.qty, row.appliedRate, row.total, row._id, row.skuCode)
        // let _row = createData(rowCount, selected.name, selected.description, "", selected.hsnCode, selected.salesUnit, 1, selected.salesPrice, total, "", selected._id)
        // row.discount, row.amount, row.igst, row.igsttotal, row.cgst, row.cgsttotal, row.sgst, row.sgsttotal, 
        setRows((rows: any) => ([...rows, _row]));
      });
    }
  }, [rowsData])


  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSelected('');
    setNewOption('');
    await dispatch(fetchAccountSkuByName(value)).unwrap().then((data) => {
      if (data) {
        console.log(data);
        const d = data.inventories.filter(function (item) {
          console.log(item)
          return item._id !== selectedAccount._id;
        });

        console.log("setting up options");
        console.log(d);

        setOptions(d);
      }
    });
  };

  const onChangeHandler = (event: any, value: any) => {
    setNewOption('');
    setIsLoading(true);
    setSelected(value);
  }

  const handleRenderOption = (e: any, b: any, c: any) => {
    console.log('b.counterPartyOfAccount: ', b.counterPartyOfAccount, b.name);
    return <div {...e} className='auto-item' key={Math.random()}>
      <div className='img-wrapper'>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          variant='square'
          style={{
            width: "30px",
            height: "30px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.name.toString().substr(0, 1).toUpperCase()}</Avatar>
      </div>
      <div><b style={{ 'marginRight': '4px' }}>
        {b.name}
      </b>
      </div>
    </div>
  };

  return (
    <>
    <Typography className={'cardHeader'} variant="h4" component="div"><Box>{"Line Items"}</Box></Typography>
    <StyledSectionTable>
    <Table className={'table'}>
        <TableHead>
          <TableRow>
            <TableCell title="Name" style={{width: '15%'}} align="left">ITEM</TableCell>
            <TableCell title="Description" style={{width: '15%'}} align="left">Description</TableCell>
            <TableCell title="HSN/SAC Code" style={{width: '7%'}} align="left">HSN/SAC</TableCell>
            {/* <TableCell title="Specification" style={{width: '12%'}} align="left">Specification</TableCell> */}
            <TableCell style={{width: '5%'}} align="center">Unit</TableCell>
            <TableCell title="Qty" style={{width: '5%'}} align="center">Qty</TableCell>
            <TableCell title="Rate" style={{width: '9%'}} align="center">Rate</TableCell>
            {/* <TableCell title="Total (Rs.)" style={{width: '7%'}} align="center">Amount</TableCell> */}
            <TableCell title="Total (Rs.)" style={{width: '7%'}} align="center">Total</TableCell>
            <TableCell title="Actions" style={{width: '7%'}} align="left">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id}>
              <CustomTableCell {...{ row, name: "name", onChange, align: "left", type:"text" }} />
              <CustomTableCell {...{ row, name: "description", onChange, align: "left", type:"text" }} />
              <CustomTableCell {...{ row, name: "hsnCode", onChange, align: "left", type:"text" }} />
              {/* <CustomTableCell {...{ row, name: "spec", onChange, align: "left", type:"text" }} /> */}
              <CustomTableCell {...{ row, name: "unit", onChange, align: "left", type:"text" }} />
              <CustomTableCell {...{ row, name: "qty", onChange, align: "center", type:"number" }} />
              <CustomTableCell {...{ row, name: "appliedRate", onChange, align: "center", type:"number", formatNumber: true }} />
              {/* <CustomTableCell {...{ row, name: "amount", onChange, align: "center", type:"number" }} /> */}
              <CustomTableCell {...{ row, name: "total", onChange, align: "center", type:"number", formatNumber: true, readonly: true }} />
              <TableCell className={'selectTableCell'}>
                {row.isEditMode ? (
                  <>
                    <IconButton
                      aria-label="done"
                      onClick={() => { 
                        if(row.name && row.description) {
                          onToggleEditMode(row.id) 
                        } 
                        else {
                          toast.error("Please enter Item and Description to create a Line Item");
                        }
                      }
                    }
                    >
                      <Check color="success" />
                    </IconButton>
                    <IconButton
                      aria-label="revert"
                      onClick={() => {
                        if(row.name && row.description) 
                          onRevert(row.id); 
                        else 
                          onDelete(row.id); 
                      }}
                    >
                      <Clear color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton
                      aria-label="Edit"
                      onClick={() => onToggleEditMode(row.id)}
                    >
                      <Edit color="success"/>
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                      onClick={() => onDelete(row.id)}
                    >
                      <Delete color="error"/>
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* <Tooltip title="Add New Line Item">
      <Button style={{ marginTop: '16px', marginBottom: '8px' }} className={'addButtonRoot'}  onClick={onAdd}>
         <AddBox /> 
      </Button>
      </Tooltip> */}
      </StyledSectionTable>
      <Box>
        <Grid container style={{ justifyContent: 'flex-start', padding: '20px', backgroundColor: '' }}>
          {/* add form fields */}

          {/* <Grid item xs={12}>
            <Typography variant="h5" component="div">{"Search From Inventory"}</Typography>
          </Grid> */}
          
          <Grid item xs={12} md={6} sm={6} lg={6}>
          <Autocomplete
                ref={elmRef}
                id={`Search Inventory`}
                freeSolo
                onChange={onChangeHandler}
                onInputChange={onInputChangeHandler}
                options={options}
                renderOption={handleRenderOption}
                getOptionLabel={option => option.name}
                renderInput={(params) => <CustomTextField {...params} name="search" label={`Search Name from Inventory`}/>}
              />
          </Grid>
          <Grid item xs={12} md={6} sm={6} lg={6} style={{display: "flex"}}>
            <Button variant='contained' style={{ marginTop: '16px', marginBottom: '8px', marginRight: '10px', height:'37px' }} onClick={onAdd}>Add to Line Item <AddBox /> </Button>
            <Button variant='contained' style={{ marginTop: '16px', marginBottom: '8px', marginRight: '10px', height:'37px' }} onClick={onAdd}>Create empty Line Item<AddBox /> </Button> 

          </Grid>
          {/* <Grid item xs={12} md={3} sm={3} lg={3}>
            <Button variant='contained' style={{ marginTop: '16px', marginBottom: '8px' }} onClick={onAdd}>Add to Line Item <AddBox /> </Button> 
          </Grid> */}
        </Grid>
      </Box>

      </>
  );
}

export default EstimateDeliveryTableInlineEdit;